import React from "react";

export interface RefundablePayment {
  uuid: string;
  code: string;
  total: number;
  tip?: number;
  card_transaction?: unknown;
  nfc_transaction?: unknown;
  qr_transaction?: unknown;
  palm_transaction?: unknown;
  cash_transaction?: unknown;
}

export interface PaymentDistributionStepProps {
  payments: RefundablePayment[];
  refundTotal: number;
  paymentRefunds: Record<string, number>;
  onRefundChange: (uuid: string, amount: number) => void;
}

const getPaymentMethodLabel = (payment: RefundablePayment): string => {
  if (payment.card_transaction) {
    return "Tarjeta Bancaria";
  }
  if (payment.nfc_transaction) {
    return "Tarjeta Cashless";
  }
  if (payment.qr_transaction) {
    return "QR";
  }
  if (payment.palm_transaction) {
    return "Mano";
  }
  if (payment.cash_transaction) {
    return "Efectivo";
  }
  return "Otro";
};

export const PaymentDistributionStep: React.FC<PaymentDistributionStepProps> = ({
  payments,
  refundTotal,
  paymentRefunds,
  onRefundChange,
}) => {
  const totalDistributed = Math.round(
    Object.values(paymentRefunds).reduce((sum, amount) => sum + (amount || 0), 0) * 100
  ) / 100;
  const remaining = Math.round((refundTotal - totalDistributed) * 100) / 100;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Distribuye el monto del reembolso entre los métodos de pago.
      </p>
      <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
        <span className="text-sm font-medium text-gray-700">
          Total a reembolsar:
        </span>
        <span className="text-lg font-bold text-red-600">
          ${refundTotal.toFixed(2)}
        </span>
      </div>
      <div className="space-y-3">
        {payments.map((payment) => {
          const refundable = payment.total - (payment.tip || 0);
          const refundableRounded = Math.round(refundable * 100) / 100;
          return (
            <div
              key={payment.uuid}
              className="space-y-2 rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {getPaymentMethodLabel(payment)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {payment.code} — Total: ${refundable.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">$</span>
                <input
                  type="number"
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="0.00"
                  min={0}
                  max={refundableRounded}
                  step="0.01"
                  value={paymentRefunds[payment.uuid] || ""}
                  onChange={(e) => {
                    const raw = parseFloat(e.target.value) || 0;
                    const value = Math.round(raw * 100) / 100;
                    onRefundChange(
                      payment.uuid,
                      Math.min(value, refundableRounded),
                    );
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div
        className={`rounded-lg p-3 ${
          Math.abs(remaining) < 0.01
            ? "bg-green-50 text-green-700"
            : "bg-yellow-50 text-yellow-700"
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Restante:</span>
          <span className="text-sm font-bold">${remaining.toFixed(2)}</span>
        </div>
        {Math.abs(remaining) >= 0.01 && (
          <p className="mt-1 text-xs">
            La suma de los reembolsos debe ser igual a ${refundTotal.toFixed(2)}.
          </p>
        )}
      </div>
    </div>
  );
};
